import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/articles";
import { formatDateShort } from "@/utils/format";

type Variant = "large" | "standard" | "horizontal" | "minimal";

type Props = {
  article: Article;
  variant?: Variant;
};

export default function ArticleCard({ article, variant = "standard" }: Props) {
  const category = getCategoryBySlug(article.categorySlug);
  const to = `/insights/${article.slug}`;

  if (variant === "minimal") {
    return (
      <article className="group border-t border-background-200 py-4 first:border-t-0 first:pt-0">
        <Link to={to} className="block">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-primary-600">{category?.name}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={article.date}>{formatDateShort(article.date)}</time>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
        </Link>
      </article>
    );
  }

  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4 border-b border-background-200 pb-5 last:border-b-0 last:pb-0">
        <Link
          to={to}
          className="relative h-[104px] w-[150px] shrink-0 overflow-hidden rounded-md bg-background-200"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">{category?.name}</span>
            <time dateTime={article.date}>{formatDateShort(article.date)}</time>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={to} className="transition-colors group-hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.dek}</p>
          <p className="mt-2 text-xs text-foreground-500">
            {article.author} · {article.readMinutes} 分钟读完
          </p>
        </div>
      </article>
    );
  }

  const isLarge = variant === "large";

  return (
    <article
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <Link
        to={to}
        className={`relative block overflow-hidden bg-background-200 ${isLarge ? "h-[300px] md:h-[380px]" : "h-[180px]"}`}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-medium text-primary-700">
          {category?.name}
        </span>
      </Link>
      <div className={`flex flex-1 flex-col ${isLarge ? "p-5 md:p-6" : "p-4 md:p-5"}`}>
        <h3
          className={`font-heading font-semibold leading-snug text-foreground-950 ${
            isLarge ? "text-xl md:text-2xl" : "text-base md:text-lg"
          }`}
        >
          <Link to={to} className="transition-colors group-hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className={`mt-3 leading-relaxed text-foreground-600 ${isLarge ? "text-sm md:text-base" : "line-clamp-3 text-sm"}`}>
          {article.dek}
        </p>
        <div className="mt-4 flex items-center gap-3 pt-1 text-xs text-foreground-500">
          <span className="text-foreground-700">{article.author}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={article.date}>{formatDateShort(article.date)}</time>
          <span aria-hidden="true">·</span>
          <span>{article.readMinutes} 分钟</span>
        </div>
      </div>
    </article>
  );
}